/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import {
  AddressPicker,
  ErrorBox,
  Flexbox,
  Form,
  LoadManager,
} from '@/components'
import useClinicalCare from '@/hooks/useClinicalCare'
import usePageTitle from '@/hooks/usePageTitle'
import yup from '@/lib/yupPt'
import { setClinicalCare } from '@/reducers/clinicalCare'
import clinicalCares from '@/services/api/clinicalCares'
import React, {
  useCallback, useEffect, useLayoutEffect, useState,
} from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FileUploader } from 'react-drag-drop-files'
import upload from '@/services/api/upload'
import PropTypes from 'prop-types'

const fileTypes = ['JPG', 'PNG', 'PDF']

const getClinicalCareClinicId = (clinicalCare) => (
  clinicalCare?.clinicId
  || clinicalCare?.clinic?.id
  || clinicalCare?.clinic?.licensedId
  || null
)

const getClinicalCareScheduledTo = (clinicalCare) => (
  clinicalCare?.scheduledTo
  || clinicalCare?.eventData?.scheduledTo
  || null
)

const getLicensingClinicId = (licensing) => (
  licensing?.licensedId
  || licensing?.licensed?.id
  || licensing?.clinicId
  || licensing?.id
  || null
)

const schema = yup.object().shape({
  clinic: yup
    .object()
    .shape({
      id: yup.number().required(),
    })
    .required(),
  scheduledTo: yup.date().required(),
  description: yup.string().trim(),
  outsourcedTransport: yup.boolean(),
})
function Approve({ title }) {
  usePageTitle({ title })
  const [loading, setLoading] = useState(false)
  const [fetchingLicensings, setFetchingLicensings] = useState(false)
  const [licensings, setLicensings] = useState([])
  const [files, setFiles] = useState([])
  const { id } = useParams()
  const {
    state: { clinicalCare },
    dispatch,
  } = useClinicalCare()
  const [address, setAddress] = useState()
  const history = useHistory()

  useEffect(() => {
    if (clinicalCare?.address || clinicalCare?.clinic?.address) {
      // eslint-disable-next-line no-unused-vars, no-shadow
      const { id, ...rest } = clinicalCare?.address || clinicalCare?.clinic?.address
      setAddress(rest)
    }
  }, [clinicalCare?.address, clinicalCare?.clinic?.address])

  function onSelectFiles(params) {
    const added = Object.values(params).filter(
      (file) => !files.find((f) => f.name === file.name),
    )
    setFiles([...files, ...added])
  }

  const fetchLicensings = useCallback(async () => {
    try {
      setFetchingLicensings(true)
      const { data } = await clinicalCares.allowedClinics()
      setLicensings(data)
    } finally {
      setFetchingLicensings(false)
    }
  }, [])

  useLayoutEffect(() => {
    fetchLicensings()
  }, [fetchLicensings])

  async function onSubmit(values) {
    try {
      setLoading(true)
      const attachments = []
      for (const file of files) {
        const {
          data: { url },
        } = await upload.sendFile(file)
        attachments.push(url)
      }
      const body = {
        ...values,
        clinicId: values?.clinic?.id,
        address,
        attachments,
      }
      const { data } = await clinicalCares.approve(id, body)
      dispatch(setClinicalCare({ ...data, tutor: data?.animal?.tutor }))
      toast.success('Atendimento aprovado')
      setLoading(false)
      history.goBack()
    } catch (e) {
      setLoading(false)
      toast.error(e?.message)
    }
  }

  return (
    <fieldset disabled={loading}>
      <div className="modal active" id="modal-id">
        <a type="button" className="modal-overlay" aria-label="Close" onClick={() => history.goBack()} />
        <div className="modal-container">
          <div className="modal-header">
            <a
              type="button"
              className="btn btn-clear float-right"
              onClick={() => history.goBack()}
            />
            <div className="modal-title h5">Aprovar atendimento #{id}</div>
          </div>
          <div className="modal-body">
            <div className="content">
              <LoadManager loading={!clinicalCare}>
                <Form.Formik
                  initialValues={{
                    clinic: {
                      id: getClinicalCareClinicId(clinicalCare) || '',
                    },
                    scheduledTo: getClinicalCareScheduledTo(clinicalCare)
                      ? new Date(getClinicalCareScheduledTo(clinicalCare))
                      : null,
                    description: clinicalCare?.description || '',
                    address: clinicalCare?.address || clinicalCare?.clinic?.address || null,
                    outsourcedTransport: Boolean(
                      clinicalCare?.outsourcedTransport,
                    ),
                  }}
                  enableReinitialize
                  validationSchema={schema}
                  onSubmit={onSubmit}
                  validateOnMount
                >
                  {({
                    touched, errors, isValid, values,
                  }) => (
                    <Form.ValidationForm>
                      <div className="mb-1">
                        Confirma a aprovação para o atendimento de{' '}
                        <b className="text-primary">{clinicalCare?.animal?.name}</b>
                        ?
                      </div>
                      <div className="text-small mb-1">
                        Esta ação vai gerar um evento de aprovação, em seguida será
                        necessário levar o pet até a clínica
                      </div>
                      <div className="divider" />
                      <Form.Group
                        className={`${
                          touched?.clinic?.id && errors?.clinic?.id
                            ? 'has-error'
                            : ''
                        }`}
                      >
                        <Form.Label>
                          Selecione para qual clínica o atendimento será
                          encaminhado <span className="text-error">*</span>
                        </Form.Label>
                        {fetchingLicensings ? (
                          <div className="columns mx-1 mb-2">
                            <div className="column col-auto loading" />
                            <div className="column">
                              <cite>Buscando clínicas disponíveis</cite>
                            </div>
                          </div>
                        ) : (
                          <>
                            {!licensings.length && (
                              <ErrorBox
                                error={{
                                  icon: 'fas fa-clinic-medical fa-3x text-error',
                                  title: 'Nenhuma clínica disponível',
                                  message:
                                    'Nenhuma clínica está licenciada para receber este encaminhamento.',
                                }}
                              >
                                <Link
                                  className="btn btn-link"
                                  to="/app/clinical-cares/clinics"
                                >
                                  <i className="fas fa-plus" />
                                  &nbsp;Gerenciar licenças de clínicas
                                </Link>
                              </ErrorBox>
                            )}

                            {licensings.map((licensing) => (
                              <React.Fragment key={licensing?.id}>
                                <small>
                                  <Form.Label className="form-radio mb-2">
                                    <Form.ValidationField
                                      type="radio"
                                      name="clinic.id"
                                      value={getLicensingClinicId(
                                        licensing,
                                      )}
                                      checked={
                                        String(values?.clinic?.id)
                                        === String(
                                          getLicensingClinicId(licensing),
                                        )
                                      }
                                    />
                                    <i className="form-icon" />
                                    &nbsp;{licensing?.licensed?.fullName}
                                  </Form.Label>
                                </small>
                              </React.Fragment>
                            ))}
                          </>
                        )}
                        {touched?.clinic?.id && errors?.clinic?.id ? (
                          <Form.Hint className="text-error">
                            Selecione uma clínica para encaminhar.
                          </Form.Hint>
                        ) : null}
                      </Form.Group>

                      <div>
                        <Form.Label>
                          Você pode informar o endereço de destino do pet
                        </Form.Label>
                        <AddressPicker
                          address={address}
                          onChange={setAddress}
                          title="Informe o endereço de destino do pet"
                        />
                      </div>
                      <div>
                        <Form.Label>
                          Você pode adicionar anexos à este evento
                        </Form.Label>
                        <FileUploader
                          handleChange={onSelectFiles}
                          hoverTitle="Solte aqui"
                          multiple
                          types={fileTypes}
                        >
                          {files.length === 0 ? (
                            <div
                              className="c-hand bg-gray text-center text-dark"
                              style={{ paddingTop: 30, paddingBottom: 30 }}
                            >
                              <div className="empty-icon">
                                <i className="fas fa-paperclip fa-lg" />
                              </div>
                              <div className="empty-title h5">
                                Selecione os anexos
                              </div>
                              <div className="empty-subtitle">
                                Clique ou arraste os anexos para adicionar
                              </div>
                            </div>
                          ) : (
                            <div
                              className="c-hand bg-gray text-center"
                              style={{ paddingTop: 30, paddingBottom: 30 }}
                            >
                              <div className="empty-title h5">
                                Anexos adicionados: {files.length}
                              </div>
                              <div className="empty-subtitle">
                                Clique ou arraste anexos para adicionar mais
                              </div>
                            </div>
                          )}
                        </FileUploader>
                      </div>

                      <div className="divider" />
                      <Flexbox.Columns>
                        <Flexbox.Column className="col-12">
                          <Form.Group
                            className={`${
                              touched?.scheduledTo && errors?.scheduledTo
                                ? 'has-error'
                                : ''
                            }`}
                          >
                            <Form.Label htmlFor="scheduledTo">
                              Agendado para{' '}
                              <span className="text-error">*</span>
                            </Form.Label>
                            <Form.ValidationField
                              name="scheduledTo"
                              id="scheduledTo"
                            >
                              {({ field }) => (
                                <Form.DatePickerField
                                  name="scheduledTo"
                                  dateFormat="dd/MM/yyyy HH:mm"
                                  minDate={new Date()}
                                  showTimeSelect
                                  className="form-input"
                                  id="scheduledTo"
                                  placeholderText="Data e hora"
                                  selected={field.value || null}
                                />
                              )}
                            </Form.ValidationField>
                            {touched?.scheduledTo && errors?.scheduledTo ? (
                              <Form.Hint className="text-error">
                                Informe a data e hora de agendamento.
                              </Form.Hint>
                            ) : null}
                          </Form.Group>
                        </Flexbox.Column>
                        <Flexbox.Column className="col-12">
                          <Form.Group
                            className={`${
                              touched?.description && errors?.description
                                ? 'has-error'
                                : ''
                            }`}
                          >
                            <Form.Label htmlFor="description">
                              Observações
                            </Form.Label>
                            <Form.ValidationField
                              name="description"
                              id="description"
                            >
                              {({ field }) => (
                                <textarea
                                  name="description"
                                  className="form-input"
                                  rows={2}
                                  placeholder="Você pode adicionar alguma observação..."
                                  // eslint-disable-next-line react/jsx-props-no-spreading
                                  {...field}
                                />
                              )}
                            </Form.ValidationField>
                            {touched?.description && errors?.description ? (
                              <Form.Hint className="text-error">
                                {errors.description}
                              </Form.Hint>
                            ) : null}
                          </Form.Group>
                        </Flexbox.Column>
                        <Flexbox.Column className="col-12">
                          <Form.Group>
                            <Form.Switch className="form-switch">
                              <Form.ValidationField
                                type="checkbox"
                                name="outsourcedTransport"
                                id="outsourcedTransport"
                              />
                              <i className="form-icon" />
                              &nbsp;O Transporte será terceirizado
                            </Form.Switch>
                            <div style={{ marginTop: -8 }}>
                              <small className="text-gray">
                                Se ativo, significa que o pet foi transportado
                                por algum terceirizado
                              </small>
                            </div>
                          </Form.Group>
                        </Flexbox.Column>
                      </Flexbox.Columns>
                      <div
                        className="modal-footer"
                        style={{ paddingLeft: 0, paddingRight: 0 }}
                      >
                        <a
                          className="btn mr-2"
                          disabled={loading}
                          onClick={() => history.goBack()}
                        >
                          <i className="fas fa-chevron-left" />
                          &nbsp;Voltar
                        </a>
                        <button
                          className={`btn btn-success ${
                            loading ? 'loading' : ''
                          }`}
                          disabled={
                            loading
                            || fetchingLicensings
                            || !licensings.length
                            || !isValid
                          }
                          type="submit"
                        >
                          <i className="fas fa-check" />
                          &nbsp;Aprovar
                        </button>
                      </div>
                    </Form.ValidationForm>
                  )}
                </Form.Formik>
              </LoadManager>
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  )
}

Approve.propTypes = {
  title: PropTypes.string.isRequired,
}

export default Approve
